// @ts-nocheck
/**
 * Cell renderer component for ContentsItem
 * @module components/ContentsItemCell
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import Circle from '@plone/volto/components/manage/Contents/circle';
import FormattedDate from '@plone/volto/components/theme/FormattedDate/FormattedDate';
import { getColor, niceBytes } from '../utils/contentsItemUtils';
import { contentsItemMessages } from '../messages/contentsItemMessages';

/**
 * Renders a cell based on the index type and item data
 */
export const ContentsItemCell = ({
  index,
  item,
  currentLang,
  organisationMapping,
  intl,
}) => {
  // Boolean type
  if (index.type === 'boolean') {
    return item[index.id] ? (
      <FormattedMessage id="Yes" defaultMessage="Yes" />
    ) : (
      <FormattedMessage id="No" defaultMessage="No" />
    );
  }

  // String type (excluding special cases)
  if (
    index.type === 'string' &&
    index.id !== 'review_state' &&
    index.id !== 'link'
  ) {
    return item[index.id];
  }

  // Link type
  if (index.id === 'link' && item[index.id]) {
    return (
      <Link
        to={item[index.id]}
        target="_blank"
        className="redirect__link"
        rel="noopener noreferrer"
      >
        {item[index.id]}
      </Link>
    );
  }

  // Review state
  if (index.id === 'review_state') {
    return (
      <div>
        <span>
          <Circle color={getColor(item[index.id])} />
        </span>
        {contentsItemMessages[item[index.id]]
          ? intl.formatMessage(contentsItemMessages[item[index.id]])
          : item['review_title'] ||
            item['review_state'] ||
            intl.formatMessage(contentsItemMessages.no_workflow_state)}
      </div>
    );
  }

  // Date type
  if (index.type === 'date') {
    return (
      <>
        {item[index?.id] && item[index.id] !== 'None' ? (
          <FormattedDate date={item[index.id]} />
        ) : (
          intl.formatMessage(contentsItemMessages.none)
        )}
      </>
    );
  }

  // Array type
  if (index.type === 'array') {
    return (
      <span>
        {item[index.id] && Array.isArray(item[index.id])
          ? item[index.id].join(', ')
          : ''}
      </span>
    );
  }

  // Contact tokens
  if (index.id === 'contact_name_tokens' && item[index.id]) {
    return (
      <span>
        {Array.isArray(item[index.id])
          ? item[index.id]
              .map((token) =>
                typeof token === 'object'
                  ? `${token.first_name || ''} ${token.last_name || ''}`.trim()
                  : token,
              )
              .join(', ')
          : ''}
      </span>
    );
  }

  // Organisation entities (special handling)
  if (index.id === 'organisation_entities' && item[index.id]) {
    return (
      <span>
        {Array.isArray(item[index.id])
          ? item[index.id]
              .map((token) => {
                let orgId;
                if (typeof token === 'object') {
                  orgId =
                    token.title ||
                    token.UID ||
                    token.id ||
                    token.organisationId;
                } else {
                  orgId = token; // z.B. "FP621"
                }

                const orgName = organisationMapping[orgId];

                if (orgName) {
                  return orgName;
                }

                return orgId; // Fallback to shortcut
              })
              .join(', ')
          : ''}
      </span>
    );
  }

  // Other tokens (excluding contact_name_tokens and organisation_entities)
  if (
    index.type === 'tokens' &&
    index.id !== 'contact_name_tokens' &&
    index.id !== 'organisation_entities' &&
    item[index.id]
  ) {
    return (
      <span>
        {Array.isArray(item[index.id])
          ? item[index.id]
              .map((token) =>
                typeof token === 'object' ? token.title || token.UID : token,
              )
              .join(', ')
          : ''}
      </span>
    );
  }

  // Image type
  if (index.type === 'image' && item['@type'] === 'Image') {
    return (
      <div className="contents-image-container">
        {/* Thumbnail */}
        <div className="contents-image-thumbnail">
          <img
            src={`${item['@id']}/@@images/image/mini`}
            alt={item.title}
            className="contents-image-thumbnail-img"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        {/* Details */}
        <div className="contents-image-details">
          {/* Dimensions */}
          {item.image_scales?.image?.[0] && (
            <div className="contents-image-info-row">
              <span className="contents-image-info-label">
                {intl.formatMessage(contentsItemMessages.dimensions)}:
              </span>
              <span className="contents-image-info-value">
                {item.image_scales.image[0].width}x
                {item.image_scales.image[0].height} px
              </span>
            </div>
          )}
          {/* Size */}
          <div className="contents-image-info-row">
            <span className="contents-image-info-label">
              {intl.formatMessage(contentsItemMessages.size)}:
            </span>
            <span className="contents-image-info-value">
              {item.getObjSize || niceBytes(item.image?.size || 0)}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Default fallback
  return null;
};

ContentsItemCell.propTypes = {
  index: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  item: PropTypes.object.isRequired,
  currentLang: PropTypes.string.isRequired,
  organisationMapping: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
};
